import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import {
  createContext,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { type MusicHandoff, saveMusicHandoff } from '@/lib/musicHandoff';
import { fitToLength } from '@/lib/musicStyle';
import { signupHrefForPath } from '@/lib/presetFromPath';

import { getMusicPresetPreview, MUSIC_PRESETS } from '@/constants/musicPresets';
import { useAnalytics } from '@/contexts/Analytics';
import { useUser } from '@/utils/useUser';

import { MUSIC_HOME_PATH } from './links';

/**
 * The page's own strings in the page's language, picked from
 * `music-generator` in getStaticProps so a page doesn't ship every page's
 * text. Empty in English (the keys are the English text).
 */
export type MusicPageCopy = Record<string, string>;

const PageCopyContext = createContext<MusicPageCopy>({});

export function MusicPageCopyProvider({
  copy,
  children,
}: {
  copy: MusicPageCopy;
  children: ReactNode;
}) {
  return (
    <PageCopyContext.Provider value={copy}>{children}</PageCopyContext.Provider>
  );
}

/**
 * The music landing pages' `t`: the page's own strings first, then the
 * shared `music-landing` ones. A key a locale hasn't translated yet falls
 * back to the English key with its {{placeholders}} filled in (next-translate
 * otherwise shows a missing key raw, placeholders and all).
 */
export function useMusicT() {
  const { t, lang } = useLocale('music-landing');
  const copy = useContext(PageCopyContext);
  return {
    lang,
    t: (key: string, query?: Record<string, unknown>) => {
      if (lang === 'en') return interpolate(key, query);
      const own = copy[key];
      if (own) return interpolate(own, query);
      return t(key, query, { default: key });
    },
  };
}

const interpolate = (text: string, query?: Record<string, unknown>) =>
  query
    ? text.replace(/{{\s*(\w+)\s*}}/g, (match, name: string) =>
        name in query ? String(query[name]) : match,
      )
    : text;

export type MusicT = ReturnType<typeof useMusicT>['t'];

export type StartSurface = 'hero' | 'idea' | 'template' | 'prompt' | 'cta';

/**
 * Starts a song from a landing page: saves the idea, template or lyrics for
 * the music generator, then opens it (or sign-up first, which hands over to
 * the music generator afterwards; see @/lib/musicHandoff).
 */
export function useStartSong(pagePath: string) {
  const router = useRouter();
  const { user } = useUser();
  const analytics = useAnalytics();
  const [busy, setBusy] = useState(false);

  const start = useCallback(
    async (
      value: Omit<MusicHandoff, 'savedAt' | 'source'>,
      surface: StartSurface,
    ) => {
      if (busy) return;
      setBusy(true);
      const saved = saveMusicHandoff({ ...value, source: pagePath });
      analytics.track('Music Landing Start', {
        page: pagePath,
        surface,
        template_id: value.templateId,
        has_prompt: Boolean(value.prompt),
        has_lyrics: Boolean(value.lyrics),
        instrumental: value.instrumental,
        signed_in: Boolean(user),
        saved,
      });
      try {
        await router.push(
          user ? '/music-generator' : signupHrefForPath(MUSIC_HOME_PATH),
        );
      } finally {
        setBusy(false);
      }
    },
    [analytics, busy, pagePath, router, user],
  );

  return { start, busy, signedIn: Boolean(user) };
}

/**
 * One shared <audio> for every preview on the page, so starting a song stops
 * the one before. Previews are plain MP3s on media.easy-peasy.ai.
 */
export function usePreviewPlayer(onStart?: (src: string) => void) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const onStartRef = useRef(onStart);
  useEffect(() => {
    onStartRef.current = onStart;
  });
  const [playingSrc, setPlayingSrc] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'none';
    audioRef.current = audio;
    // The element's own events drive the state, so a pause caused by
    // switching songs can't overwrite the song that just started.
    const onTime = () =>
      setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
    const onPlay = () => setPlayingSrc(audio.src);
    const onPause = () => setPlayingSrc(null);
    const onEnd = () => {
      setPlayingSrc(null);
      setProgress(0);
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnd);
    return () => {
      audio.pause();
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnd);
      audioRef.current = null;
    };
  }, []);

  const toggle = useCallback((src: string) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.src === src && !audio.paused) {
      audio.pause();
      return;
    }
    if (audio.src !== src) {
      audio.src = src;
      setProgress(0);
      onStartRef.current?.(src);
    }
    audio.play().catch(() => setPlayingSrc(null));
  }, []);

  return { playingSrc, progress, toggle };
}

export type PreviewPlayer = ReturnType<typeof usePreviewPlayer>;

const PlayerContext = createContext<PreviewPlayer | null>(null);

/**
 * One preview player for the whole page (hero, sound wall, examples). Each
 * preview's first play is tracked, so we can see whether people listen.
 */
export function MusicPlayerProvider({
  pagePath,
  children,
}: {
  pagePath: string;
  children: ReactNode;
}) {
  const analytics = useAnalytics();
  const player = usePreviewPlayer((src) => {
    const preset = MUSIC_PRESETS.find(
      (item) => getMusicPresetPreview(item.id)?.audio === src,
    );
    analytics.track('Music Landing Preview Played', {
      page: pagePath,
      template_id: preset?.id,
    });
  });
  return (
    <PlayerContext.Provider value={player}>{children}</PlayerContext.Provider>
  );
}

export function useMusicPlayer(): PreviewPlayer {
  const player = useContext(PlayerContext);
  if (!player) throw new Error('useMusicPlayer outside MusicPlayerProvider');
  return player;
}

/**
 * What the hero box takes on the visitor's plan, the same limits the
 * generator checks (new accounts start on Free).
 */
export function useBoxLimits() {
  const { user, isFreePlan } = useUser();
  const paid = Boolean(user) && !isFreePlan;
  return {
    idea: paid ? 400 : 100,
    lyrics: paid ? 3000 : 400,
    style: paid ? 1000 : 200,
  };
}

type SongIdeaState = {
  idea: string;
  setIdea: (value: string) => void;
  lyrics: string;
  setLyrics: (value: string) => void;
  style: string;
  setStyle: (value: string) => void;
  instrumental: boolean;
  setInstrumental: (value: boolean) => void;
  limits: ReturnType<typeof useBoxLimits>;
  /** The hero box's main field (description, or lyrics on the lyrics page). */
  boxRef: RefObject<HTMLTextAreaElement | null>;
  /** Puts a text in the hero box (cut to the limit) and takes the visitor there. */
  fillIdea: (text: string, instrumental?: boolean) => void;
  /** Puts a style in the lyrics box's Style field and takes the visitor there. */
  fillStyle: (style: string) => void;
};

const SongIdeaContext = createContext<SongIdeaState | null>(null);

/** The hero box's fields, shared so other sections can fill them. */
export function SongIdeaProvider({
  instrumental: startInstrumental,
  children,
}: {
  instrumental: boolean;
  children: ReactNode;
}) {
  const [idea, setIdea] = useState('');
  const [lyrics, setLyrics] = useState('');
  const [style, setStyle] = useState('');
  const [instrumental, setInstrumental] = useState(startInstrumental);
  const limits = useBoxLimits();
  const boxRef = useRef<HTMLTextAreaElement>(null);

  const showBox = useCallback(() => {
    const box = boxRef.current;
    if (!box) return;
    box.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => box.focus({ preventScroll: true }), 400);
  }, []);

  const fillIdea = useCallback(
    (text: string, nextInstrumental?: boolean) => {
      setIdea(fitToLength(text, limits.idea));
      if (typeof nextInstrumental === 'boolean') {
        setInstrumental(nextInstrumental);
      }
      showBox();
    },
    [limits.idea, showBox],
  );

  const fillStyle = useCallback(
    (value: string) => {
      setStyle(fitToLength(value, limits.style));
      showBox();
    },
    [limits.style, showBox],
  );

  return (
    <SongIdeaContext.Provider
      value={{
        idea,
        setIdea,
        lyrics,
        setLyrics,
        style,
        setStyle,
        instrumental,
        setInstrumental,
        limits,
        boxRef,
        fillIdea,
        fillStyle,
      }}
    >
      {children}
    </SongIdeaContext.Provider>
  );
}

export function useSongIdea(): SongIdeaState {
  const state = useContext(SongIdeaContext);
  if (!state) throw new Error('useSongIdea outside SongIdeaProvider');
  return state;
}

/** "2:40" for a duration in seconds. */
export const formatDuration = (seconds: number) => {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

/** Every generation makes two versions and uses this many characters. */
export const CHARACTERS_PER_GENERATION = 600;

const formatter = (lang: string) =>
  new Intl.NumberFormat(lang === 'ar' ? 'ar-u-nu-latn' : lang);

export const formatCount = (value: number, lang: string) =>
  formatter(lang).format(value);
